import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'motion/react';
import { Trophy, ChevronDown, ChevronRight, ChevronsRight, FileText, Download, Trash2, MessageCircle, Share2, Mars, Venus, CalendarDays, Users, DollarSign, Plus, X as XIcon, RotateCcw, Settings, Edit, MoreVertical, HelpCircle, MapPin, Clock, CheckCircle2, AlertTriangle } from 'lucide-react';
import { doc, getDoc, updateDoc, deleteDoc, collection, addDoc, setDoc, arrayUnion, writeBatch, deleteField } from 'firebase/firestore';
import { ref, uploadString, getDownloadURL } from 'firebase/storage';
import { auth, storage } from '../../lib/firebase';
import { Tournament, Team, Match, Club, Config, BracketMatch, Player, UserProfile, RankingConfig } from '../../types';
import { ChampionBadge } from '../common/ChampionBadge';
import { SearchableTeamSelect } from '../common/SearchableTeamSelect';
import { AdminTournamentZone } from './AdminTournamentZone';
import { SymmetricBracket } from '../tournament/SymmetricBracket';
import { AdminMatchEditModal } from './AdminMatchEditModal';
import { AdminTournamentLobby } from './AdminTournamentLobby';
import { RankingService } from '../../services/rankingService';
import { StatsService } from '../../services/statsService';
import { exportZonesAsImage } from '../../lib/canvas-utils';
import { logActivity } from '../../services/activityLogger';
import { ALL_CITIES } from '../../data/locations';

interface AdminTournamentCardProps {
  key?: any;
  index: number;
  t: Tournament;
  allTeams: Team[];
  players: Player[];
  matches: Match[];
  clubs: Club[];
  db: any;
  getCollectionPath: (col: string) => string;
  config: Config;
  rankingConfig: RankingConfig;
  handleShareCanvas: (canvas: HTMLCanvasElement, filename: string, action: 'download' | 'whatsapp') => void;
  onPlayerClick: (id: string | null) => void;
  isExpanded: boolean;
  onToggle: () => void;
  isSelected?: boolean;
  sedesPublicas?: UserProfile[];
}

export const AdminTournamentCard = React.memo(function AdminTournamentCard({
  index,
  t,
  allTeams,
  players,
  matches,
  clubs,
  db,
  getCollectionPath,
  config,
  rankingConfig,
  handleShareCanvas,
  onPlayerClick,
  isExpanded,
  onToggle,
  isSelected,
  sedesPublicas = [],
}: AdminTournamentCardProps) {
  const [showAddBracket, setShowAddBracket] = useState(false);
  const [showBracketHelp, setShowBracketHelp] = useState(false);
  
  const showSuccessToast = (message: string) => {
    const el = document.createElement('div');
    el.className = "fixed top-12 left-1/2 -translate-x-1/2 z-[999999] bg-[#1a1c23]/95 backdrop-blur-md text-emerald-400 px-6 py-4 rounded-2xl font-black text-[11px] md:text-sm shadow-[0_10px_40px_rgba(16,185,129,0.3)] flex items-center gap-3 animate-in slide-in-from-top-10 fade-in duration-300 border border-emerald-500/20 uppercase tracking-widest text-center";
    el.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="shrink-0 animate-bounce"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg><span>${message}</span>`;
    document.body.appendChild(el);
    setTimeout(() => {
      el.style.opacity = '0';
      el.style.transition = 'opacity 0.3s ease';
      setTimeout(() => el.remove(), 300);
    }, 4000);
  };
  const [showResetBracketConfirm, setShowResetBracketConfirm] = useState(false);
  const [confirmAdvance, setConfirmAdvance] = useState(false);
  const [numBracketTeams, setNumBracketTeams] = useState('8');
  const [bracketGenMode, setBracketGenMode] = useState<'manual' | 'auto'>('auto');
  const [showRulesEdit, setShowRulesEdit] = useState(false);
  const [rulesText, setRulesText] = useState(t.rulesText || '');
  const [rulesPdfUrl, setRulesPdfUrl] = useState(t.rulesPdfUrl || '');
  const [editingDateTime, setEditingDateTime] = useState<{bIdx: number, mIdx: number, match: BracketMatch} | null>(null);
  const [bracketDate, setBracketDate] = useState('');
  const [bracketTime, setBracketTime] = useState('');
  const [eliminationStartDate, setEliminationStartDate] = useState(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  });
  const [eliminationDaysPerRound, setEliminationDaysPerRound] = useState(t.scheduleDaysPerRound || 4);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [showActionMenu, setShowActionMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowActionMenu(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const [lobbyIsEditingInfo, setLobbyIsEditingInfo] = useState(false);
  const [lobbyShowExitConfirm, setLobbyShowExitConfirm] = useState(false);
  const [lobbyHasChanges, setLobbyHasChanges] = useState(false);
  const lobbySaveTriggerRef = useRef<(() => Promise<void>) | null>(null);
  const lobbyDiscardTriggerRef = useRef<(() => void) | null>(null);

  // Consider it "newly created" if it was created in the last 2 minutes
  const isNew = t.createdAt && (Date.now() - t.createdAt < 120000);

  const [processedLogo, setProcessedLogo] = useState<string | null>(null);

  const isLobby = t.status === 'Lobby' || t.status === 'Registro' || t.status === 'Borrador' || !t.status;
  const isActiveStatus = t.status === 'En Juego' || t.status === 'Fase de Grupos' || t.status === 'Eliminatorias';
  const registeredCount = t.registeredTeamIds?.length || 0;
  const clubOrganizer = sedesPublicas.find(s => s.uid === t.organizerId);
  const playerOrganizer = players.find(p => p.id === t.organizerId);
  const isSuperAdminOrganizer = clubOrganizer?.role === 'superadmin' || 
    t.organizerId === 'admin' || 
    t.organizerId === 'main_club' || 
    t.organizerId === 'agunavarro1989@gmail.com' ||
    playerOrganizer?.email === 'agunavarro1989@gmail.com' ||
    playerOrganizer?.name?.toLowerCase().includes('agustin navarro');

  const organizerName = isSuperAdminOrganizer ? config.pageName : (playerOrganizer?.organizerProfile?.name || clubOrganizer?.name || playerOrganizer?.name || 'Torneo');
  const logoUrl = isSuperAdminOrganizer ? config.logoUrl : (playerOrganizer?.organizerProfile?.logoUrl || clubOrganizer?.photoUrl || config.logoUrl);
  useEffect(() => {
    let isActive = true;

    if (!logoUrl) {
      if (isActive) setProcessedLogo(null);
      return;
    }
    
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.src = logoUrl;
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(img, 0, 0);
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;
        // Chroma-keying out black (R,G,B < 35)
        for (let i = 0; i < data.length; i += 4) {
          if (data[i] < 35 && data[i+1] < 35 && data[i+2] < 35) {
            data[i+3] = 0;
          }
        }
        ctx.putImageData(imageData, 0, 0);
        if (isActive) {
          setProcessedLogo(canvas.toDataURL());
        }
      }
    };
    img.onerror = () => {
      if (isActive) {
        setProcessedLogo(logoUrl);
      }
    };

    return () => {
      isActive = false;
    };
  }, [logoUrl]);

  const champion = t.championId ? allTeams.find((team) => team.id === t.championId) : null;
  const isEven = index % 2 === 0;
  const [editingMatch, setEditingMatch] = useState<{ bIdx: number; mIdx: number; match: BracketMatch } | null>(null);

  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [isThirdPlaceActive, setIsThirdPlaceActive] = useState(false);
  const [modalRankings, setModalRankings] = useState<{ teamId: string, rank: number, points: number }[]>([]);

  const toggleThirdPlace = () => {
    setIsThirdPlaceActive(!isThirdPlaceActive);
    if (!isThirdPlaceActive) {
      // Activating 3rd place: change the first rank=4 to rank=3, and filter out the second rank=4
      setModalRankings(prev => {
        const first = prev.find(r => r.rank === 1) || { teamId: '', rank: 1, points: rankingConfig.champion };
        const second = prev.find(r => r.rank === 2) || { teamId: '', rank: 2, points: rankingConfig.runnerUp };
        const third = { teamId: '', rank: 3, points: rankingConfig.awardPointsTo === 'third_place' ? (rankingConfig.thirdPlace || 0) : rankingConfig.semifinalist };
        return [first, second, third];
      });
    } else {
      // Deactivating 3rd place: revert to two rank=4 (Semifinalists)
      setModalRankings(prev => {
        const first = prev.find(r => r.rank === 1) || { teamId: '', rank: 1, points: rankingConfig.champion };
        const second = prev.find(r => r.rank === 2) || { teamId: '', rank: 2, points: rankingConfig.runnerUp };
        const sf1 = { teamId: '', rank: 4, points: rankingConfig.awardPointsTo === 'third_place' ? 0 : rankingConfig.semifinalist };
        const sf2 = { teamId: '', rank: 4, points: rankingConfig.awardPointsTo === 'third_place' ? 0 : rankingConfig.semifinalist };
        return [first, second, sf1, sf2];
      });
    }
  };

  const handleOpenArchiveModal = () => {
    let finalMatch = null;
    let semiMatches: any[] = [];
    
    if (t.brackets && t.brackets.length > 0) {
      const finalRound = t.brackets[t.brackets.length - 1];
      if (finalRound && finalRound.matches && finalRound.matches.length > 0) {
        finalMatch = finalRound.matches[0];
      }
      if (t.brackets.length > 1) {
        const semiRound = t.brackets[t.brackets.length - 2];
        if (semiRound && semiRound.matches && semiRound.matches.length > 0) {
          semiMatches = semiRound.matches.filter((m: any) => m.team1Id !== 'BYE' && m.team2Id !== 'BYE');
        }
      }
    }

    const thirdMatch = t.thirdPlaceMatch;

    let firstId = '';
    let secondId = '';
    let thirdId = '';
    let sf1Id = '';
    let sf2Id = '';
    let hasThirdPlace = false;

    if (finalMatch && finalMatch.winnerId) {
      firstId = finalMatch.winnerId;
      secondId = finalMatch.winnerId === finalMatch.team1Id ? (finalMatch.team2Id || '') : (finalMatch.team1Id || '');
    }

    if (thirdMatch && thirdMatch.winnerId) {
      thirdId = thirdMatch.winnerId;
      hasThirdPlace = true;
    } else if (semiMatches.length === 2) {
      const getLoser = (m: any) => {
        if (!m.winnerId) return '';
        return m.winnerId === m.team1Id ? (m.team2Id || '') : (m.team1Id || '');
      };
      sf1Id = getLoser(semiMatches[0]);
      sf2Id = getLoser(semiMatches[1]);
    }

    if (hasThirdPlace) {
      setIsThirdPlaceActive(true);
      setModalRankings([
        { teamId: firstId, rank: 1, points: rankingConfig.champion },
        { teamId: secondId, rank: 2, points: rankingConfig.runnerUp },
        { teamId: thirdId, rank: 3, points: rankingConfig.awardPointsTo === 'third_place' ? (rankingConfig.thirdPlace || 0) : rankingConfig.semifinalist }
      ]);
    } else {
      setIsThirdPlaceActive(false);
      setModalRankings([
        { teamId: firstId, rank: 1, points: rankingConfig.champion },
        { teamId: secondId, rank: 2, points: rankingConfig.runnerUp },
        { teamId: sf1Id, rank: 4, points: rankingConfig.awardPointsTo === 'third_place' ? 0 : rankingConfig.semifinalist },
        { teamId: sf2Id, rank: 4, points: rankingConfig.awardPointsTo === 'third_place' ? 0 : rankingConfig.semifinalist }
      ]);
    }

    setShowArchiveModal(true);
  };

  const handleArchive = async () => {
    // Fill first place champion ID (required)
    const firstPlace = modalRankings.find(r => r.rank === 1);
    if (!firstPlace?.teamId) {
      alert('Por favor, selecciona al menos el 1er Puesto (Campeón).');
      return;
    }

    try {
      setShowArchiveModal(false);
      if (isExpanded) onToggle();
      showSuccessToast('¡Torneo finalizado! (Procesando en segundo plano y movido al historial)');

      // 1. Update Tournament Status
      await updateDoc(doc(db, getCollectionPath('tournaments'), t.id), {
        isArchived: true,
        championId: firstPlace.teamId,
      });

      // 2. Assign achievements AND fixed ranking points to ALL participating players
      const validRankings = modalRankings.filter(r => r.teamId);

      // Get all teams in this tournament (including those manually added to brackets/zones)
      const tournamentTeamsIds = new Set<string>();
      (t.registeredTeamIds || []).forEach(id => tournamentTeamsIds.add(id));
      validRankings.forEach(r => tournamentTeamsIds.add(r.teamId));
      t.brackets?.forEach(b => b.matches.forEach(m => {
        if (m.team1Id && m.team1Id !== 'BYE') tournamentTeamsIds.add(m.team1Id);
        if (m.team2Id && m.team2Id !== 'BYE') tournamentTeamsIds.add(m.team2Id);
      }));
      t.zones?.forEach(z => z.teams.forEach(tm => tournamentTeamsIds.add(tm.teamId)));
      const tournamentTeams = allTeams.filter(tm => tournamentTeamsIds.has(tm.id));

      for (const team of tournamentTeams) {
        // Determine rank and points
        const rankInfo = validRankings.find(r => r.teamId === team.id);
        const finalRank = rankInfo ? rankInfo.rank : 99; // 99 for generic participation

        let pointsToAward = rankInfo?.points ?? 0;

        // Achievements only for top 3
        const achievementType: 'oro' | 'plata' | 'bronce' | null =
          finalRank === 1 ? 'oro' :
            finalRank === 2 ? 'plata' :
              finalRank === 3 ? 'bronce' : null;

        const achievement = achievementType ? {
          type: achievementType,
          text: `${finalRank}° Puesto - ${t.name} (${t.category || ''})`
        } : null;

        // Update both players
        const playerIds = [team.player1Id, team.player2Id].filter(Boolean);
        for (const pId of playerIds) {
          const playerRef = doc(db, getCollectionPath('players'), pId);
          
          // 1. Add achievement if applicable
          if (achievement) {
            await updateDoc(playerRef, {
              achievements: arrayUnion(achievement)
            }).catch(err => console.error(`Error updating player ${pId} achievement:`, err));
          }

          // 2. Add ranking points to history
          const historyRef = collection(db, getCollectionPath('players'), pId, 'pointsHistory');
          await addDoc(historyRef, {
            points: pointsToAward,
            type: 'tournament',
            tournamentId: t.id,
            organizerId: t.organizerId || auth.currentUser?.uid || 'legacy_organizer',
            isGlobal: t.isMaster || t.category === 'MASTER' || t.name.toLowerCase().includes('master'), // Simple heuristic for now
            reason: finalRank <= 4 ? `Torneo: ${t.name} (${finalRank <= 2 ? (finalRank === 1 ? 'Campeón' : 'Finalista') : (finalRank === 3 ? '3er Puesto' : 'Semi')})` : `Participación: ${t.name}`,
            createdAt: Date.now()
          }).catch(err => console.error(`Error adding history for player ${pId}:`, err));

          // 3. Send Notification
          const rankLabel = finalRank === 1 ? '¡CAMPEÓN!' : (finalRank === 2 ? 'Finalista' : (finalRank === 3 ? '3er Puesto' : (finalRank === 4 ? 'Semifinalista' : 'Participación')));
          await addDoc(collection(db, getCollectionPath('notifications')), {
            userId: pId,
            title: `Torneo: ${rankLabel}`,
            body: `¡Felicidades! Has conseguido +${pointsToAward} puntos en ${t.name}.`,
            points: pointsToAward,
            type: 'ranking_points',
            matchId: t.id,
            read: false,
            createdAt: Date.now()
          }).catch(err => console.error(`Error sending notification to player ${pId}:`, err));

          // 4. Trigger recalculation
          await RankingService.recalculatePlayerPoints(pId).catch(err => console.error(`Error recalculating points for ${pId}:`, err));
        }
      }
    } catch (error) {
      console.error('Error archiving tournament:', error);
      alert('Error al archivar el torneo.');
    }
  };

  const saveRules = async () => {
    setUploadingPdf(true);
    let finalRulesPdfUrl = rulesPdfUrl;
    
    try {
      if (finalRulesPdfUrl.startsWith('data:application/pdf')) {
        const pdfRef = ref(storage, `tournaments/${t.id}_rules_${Date.now()}.pdf`);
        await uploadString(pdfRef, finalRulesPdfUrl, 'data_url');
        finalRulesPdfUrl = await getDownloadURL(pdfRef);
      }

      await updateDoc(doc(db, getCollectionPath('tournaments'), t.id), {
        rulesText,
        rulesPdfUrl: finalRulesPdfUrl,
        description: t.description // Preserve descriptions
      });
      setShowRulesEdit(false);
    } catch (err) {
      console.error('Error saving rules:', err);
      alert('Error al guardar las reglas o subir el PDF.');
    } finally {
      setUploadingPdf(false);
    }
  };

  
  const safeDeleteMatch = async (matchId: string) => {
    try {
      const matchRef = doc(db, getCollectionPath('matches'), matchId);
      const snap = await getDoc(matchRef);
      if (snap.exists()) {
        const data = snap.data();
        if (data.status === 'completed') {
          await RankingService.revertMatchPoints(matchId, data.participantIds || []);
        }
        await deleteDoc(matchRef);
        if (data.participantIds) {
          for (const pid of data.participantIds) {
            await StatsService.recalculatePlayerStats(pid);
          }
        }
      } else {
        await deleteDoc(matchRef).catch(() => {});
      }
    } catch (e) {
      console.error('Error safely deleting match', e);
    }
  };

  const handleDeleteTournament = async () => {
    await deleteDoc(doc(db, getCollectionPath('tournaments'), t.id));
    if (matches) {
      const tMatches = matches.filter((m) => m.tournamentId === t.id);
      for (const m of tMatches) {
        await safeDeleteMatch(m.id);
      }
    }

    if (t.locationClubIds && Array.isArray(t.locationClubIds)) {
      for (const clubId of t.locationClubIds) {
        try {
          const scheduleRef = doc(db, getCollectionPath('clubSchedules'), clubId);
          const snap = await getDoc(scheduleRef);
          if (snap.exists()) {
            const data = snap.data();
            if (data.offDays) {
              const filteredOffDays = data.offDays.filter((od: any) => !od.id.startsWith(`block_${t.id}_`));
              if (filteredOffDays.length !== data.offDays.length) {
                await updateDoc(scheduleRef, { offDays: filteredOffDays });
              }
            }
          }
        } catch (e) {
          console.error("Error eliminando bloqueos de club:", e);
        }
      }
    }

    setConfirmDelete(false);
  };

  const handlePdfUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== 'application/pdf') {
      alert('Solo se permiten archivos PDF');
      return;
    }

    if (file.size > 800 * 1024) {
      alert('El PDF es demasiado grande. Por favor sube un archivo menor a 800 KB o pega un enlace externo en la caja de abajo.');
      return;
    }

    setUploadingPdf(true);
    const reader = new FileReader();
    reader.onload = (event) => {
      setRulesPdfUrl(event.target?.result as string);
      setUploadingPdf(false);
    };
    reader.onerror = () => {
      alert('Ocurrió un error al procesar el PDF.');
      setUploadingPdf(false);
    };
    reader.readAsDataURL(file);
  };

  const generateBracket = async (isAuto: boolean = false) => {
    const num = parseInt(numBracketTeams);
    let rounds: any[] = [];
    let currentMatches = num / 2;
    const roundNames: any = { 8: 'Octavos de Final', 4: 'Cuartos de Final', 2: 'Semifinal', 1: 'Final' };
    const batch = writeBatch(db);

    while (currentMatches >= 1) {
      const gMatches = Array.from({ length: currentMatches }).map(() => ({
        team1Id: '',
        team2Id: '',
        s1: '',
        s2: '',
        s3: '',
        score: '',
        winnerId: '',
        t1: '',
        t2: '',
        isPublished: false
      }));
      rounds.push({
        round: roundNames[currentMatches] || `Ronda de ${currentMatches * 2}`,
        matches: gMatches,
      });
      currentMatches = currentMatches / 2;
    }

    if (isAuto && t.zones && t.zones.length > 0) {
      let allZoneTeams: any[] = [];
      t.zones.forEach((z: any, zIdx: number) => {
        if (z.teams) {
          const sortedZoneTeams = [...z.teams].sort((a: any, b: any) => {
            const ptsA = a.pts || 0; const ptsB = b.pts || 0;
            if (ptsB !== ptsA) return ptsB - ptsA;
            const diffA = (a.sf || 0) - (a.sc || 0);
            const diffB = (b.sf || 0) - (b.sc || 0);
            if (diffB !== diffA) return diffB - diffA;
            return (b.sf || 0) - (a.sf || 0);
          });
          sortedZoneTeams.forEach((team, pos) => {
            if (team.teamId) {
              allZoneTeams.push({ ...team, zoneIndex: zIdx, position: pos + 1 });
            }
          });
        }
      });

      allZoneTeams.sort((a, b) => {
        if (a.position !== b.position) return a.position - b.position;
        const ptsA = a.pts || 0; const ptsB = b.pts || 0;
        if (ptsB !== ptsA) return ptsB - ptsA;
        const diffA = (a.sf || 0) - (a.sc || 0);
        const diffB = (b.sf || 0) - (b.sc || 0);
        if (diffB !== diffA) return diffB - diffA;
        return (b.sf || 0) - (a.sf || 0);
      });

      const topTeams = allZoneTeams.slice(0, num);
      let finalTeams = [...topTeams];

      const seedOrderForHalf = (n: number): number[] => {
        if (n === 2) return [1, 2];
        const half = seedOrderForHalf(n / 2);
        const res: number[] = [];
        for (let seed of half) {
          res.push(seed);
          res.push(n - seed + 1);
        }
        return res;
      };

      const seedOrder = seedOrderForHalf(num);

      for (let attempt = 0; attempt < 5; attempt++) {
        let hasConflict = false;
        for (let i = 0; i < seedOrder.length; i += 2) {
          const s1 = seedOrder[i] - 1;
          const s2 = seedOrder[i + 1] - 1;
          if (finalTeams[s1] && finalTeams[s2] && finalTeams[s1].zoneIndex === finalTeams[s2].zoneIndex) {
            hasConflict = true;
            const swapTarget = s2 > s1 + 1 ? s2 - 1 : s2 + 1;
            if (swapTarget < num && finalTeams[swapTarget]) {
              const temp = finalTeams[s2];
              finalTeams[s2] = finalTeams[swapTarget];
              finalTeams[swapTarget] = temp;
            }
          }
        }
        if (!hasConflict) break;
      }

      for (let i = 0; i < seedOrder.length / 2; i++) {
        const s1 = seedOrder[i * 2] - 1;
        const s2 = seedOrder[i * 2 + 1] - 1;
        const t1Obj = finalTeams[s1];
        const t2Obj = finalTeams[s2];
        
        let t1Id = '', t1Name = '', t2Id = '', t2Name = '';
        if (t1Obj) { t1Id = t1Obj.teamId; t1Name = t1Obj.name || ''; }
        if (t2Obj) { t2Id = t2Obj.teamId; t2Name = t2Obj.name || ''; }

        let isBye = false;
        let winnerId = '';

        if (t1Obj && !t2Obj) {
          t2Id = 'BYE'; t2Name = 'PASE DIRECTO';
          isBye = true; winnerId = t1Id;
        } else if (!t1Obj && t2Obj) {
          t1Id = 'BYE'; t1Name = 'PASE DIRECTO';
          isBye = true; winnerId = t2Id;
        }

        rounds[0].matches[i].team1Id = t1Id;
        rounds[0].matches[i].t1 = t1Name;
        rounds[0].matches[i].team2Id = t2Id;
        rounds[0].matches[i].t2 = t2Name;

        if (t.managementMode === 'express' && t1Id && t2Id && t1Id !== 'BYE' && t2Id !== 'BYE') {
          rounds[0].matches[i].isPublished = true;
          const matchId = `bracket_${t.id}_0_${i}`;
          
          const t1Full = allTeams.find(team => team.id === t1Id) || null;
          const t2Full = allTeams.find(team => team.id === t2Id) || null;
          let pIds: string[] = [];
          if (t1Full) pIds.push(t1Full.player1Id, t1Full.player2Id, t1Full.player3Id || '');
          if (t2Full) pIds.push(t2Full.player1Id, t2Full.player2Id, t2Full.player3Id || '');
          pIds = pIds.filter(Boolean);

          batch.set(doc(db, getCollectionPath('matches'), matchId), {
            tournamentId: t.id,
            tournamentName: t.name,
            round: rounds[0].round || 'Fase Final',
            team1Id: t1Id,
            team2Id: t2Id,
            s1: '', s2: '', s3: '', score: '',
            date: '',
            time: '',
            winnerId: null,
            organizerId: t.organizerId || auth.currentUser?.uid || '',
            status: 'pending',
            createdAt: Date.now(),
            participantIds: pIds,
            winnerPlayerIds: [],
            team1Obj: t1Full,
            team2Obj: t2Full
          });
        }

        if (isBye) {
          rounds[0].matches[i].winnerId = winnerId;
          rounds[0].matches[i].score = 'W.O.';
          if (rounds.length > 1) {
            const nextMIdx = Math.floor(i / 2);
            const isTeam1Slot = i % 2 === 0;
            if (isTeam1Slot) {
              rounds[1].matches[nextMIdx].team1Id = winnerId;
              rounds[1].matches[nextMIdx].t1 = winnerId === t1Id ? t1Name : t2Name;
            } else {
              rounds[1].matches[nextMIdx].team2Id = winnerId;
              rounds[1].matches[nextMIdx].t2 = winnerId === t1Id ? t1Name : t2Name;
            }
          }
        }
      }
    }

    const tElimStartDate = new Date(eliminationStartDate + 'T00:00:00').getTime();

    batch.update(doc(db, getCollectionPath('tournaments'), t.id), { 
      brackets: rounds,
      status: 'Eliminatorias',
      eliminationStartDate: tElimStartDate,
      eliminationDaysPerRound: eliminationDaysPerRound
    });

    await batch.commit();

    const currentUser = auth.currentUser;
    const currentUserProfile = players.find(p => p.id === currentUser?.uid);
    const activeUserName = currentUserProfile?.name || currentUser?.displayName || currentUser?.email || 'Administrador';
    await logActivity(
      db,
      getCollectionPath,
      currentUser?.uid || 'admin',
      activeUserName,
      'admin',
      `Inicio de fase eliminatoria - Torneo: ${t.name} (${num} parejas)`
    );

    setShowAddBracket(false);
  };

  const [showLobbyConfirm, setShowLobbyConfirm] = useState(false);

  const handleBackToLobby = async () => {
    console.log('[AdminTournamentCard] handleBackToLobby triggered for tournament:', t.id);
    const hasPoints = matches.some(m => m.tournamentId === t.id && (m.winnerId || m.score || m.status === 'completed'));

    if (hasPoints) {
      console.warn('[AdminTournamentCard] Cannot go back to lobby: results detected');
      alert("No se puede volver al lobby porque ya hay partidos jugados o resultados registrados.");
      setShowLobbyConfirm(false);
      return;
    }

    try {
      console.log('[AdminTournamentCard] Executing reset to Lobby...');
      const tournamentRef = doc(db, getCollectionPath('tournaments'), t.id);

      // 1. Delete all matches associated with this tournament
      const tMatches = matches.filter(m => m.tournamentId === t.id);
      console.log(`[AdminTournamentCard] Deleting ${tMatches.length} matches...`);

      const batchSize = 500;
      for (let i = 0; i < tMatches.length; i += batchSize) {
        const batch = writeBatch(db);
        const chunk = tMatches.slice(i, i + batchSize);
        for (const m of chunk) {
          batch.delete(doc(db, getCollectionPath('matches'), m.id));
        }
        await batch.commit();
      }

      // 2. Reset status and clear generated structures
      await updateDoc(tournamentRef, {
        status: 'Lobby',
        zones: [],
        brackets: []
      });

      console.log('[AdminTournamentCard] Successfully returned to Lobby');
      setShowLobbyConfirm(false);

      // Redirect to "Inscripciones" tab
      window.dispatchEvent(new CustomEvent('changeAdminTab', {
        detail: { tab: 'inscripciones', tournamentId: t.id }
      }));
    } catch (error) {
      console.error("[AdminTournamentCard] Error returning to lobby:", error);
      alert("Error al volver al lobby.");
      setShowLobbyConfirm(false);
    }
  };

  const handleResetBracket = async () => {
    const hasResults = matches.some(m => m.tournamentId === t.id && (m.winnerId || m.score || m.status === 'completed'));
    if (hasResults) {
      alert("No se puede reiniciar las llaves porque ya hay resultados registrados. Borra los resultados primero.");
      setShowResetBracketConfirm(false);
      return;
    }

    try {
      const tournamentRef = doc(db, getCollectionPath('tournaments'), t.id);
      await updateDoc(tournamentRef, {
        brackets: [],
        thirdPlaceMatch: deleteField()
      });
      setShowAddBracket(true);
      setShowResetBracketConfirm(false);
    } catch (error) {
      console.error("Error resetting bracket:", error);
      alert("Error al reiniciar las llaves.");
    }
  };

  const handleEditMatchOpen = (bIdx: number, mIdx: number, match: BracketMatch) => {
    setEditingMatch({ bIdx, mIdx, match });
  };

  const handleDropTeamToBracket = async (bIdx: number, mIdx: number, teamId: string, teamName: string, slot: 1 | 2, sourceInfo?: { type: 'bracket', bIdx: number, mIdx: number, slot: 1 | 2 }) => {
    console.log('[AdminTournamentCard] handleDropTeamToBracket called:', { bIdx, mIdx, teamId, teamName, slot, sourceInfo });

    if (bIdx === 999) {
      const thirdMatch = JSON.parse(JSON.stringify(t.thirdPlaceMatch || { team1Id: '', team2Id: '', s1: '', s2: '', s3: '', score: '', winnerId: '' }));
      if (slot === 1) {
        thirdMatch.team1Id = teamId;
        thirdMatch.t1 = teamName;
      } else {
        thirdMatch.team2Id = teamId;
        thirdMatch.t2 = teamName;
      }
      thirdMatch.winnerId = ''; thirdMatch.score = ''; thirdMatch.s1 = ''; thirdMatch.s2 = ''; thirdMatch.s3 = ''; thirdMatch.isPublished = false;
      await updateDoc(doc(db, getCollectionPath('tournaments'), t.id), { thirdPlaceMatch: thirdMatch });
      
      if (t.managementMode === 'express' && thirdMatch.team1Id && thirdMatch.team2Id && thirdMatch.team1Id !== 'BYE' && thirdMatch.team2Id !== 'BYE' && !thirdMatch.isPublished) {
        await handlePublishMatch(999, 0, thirdMatch);
      }
      return;
    }

    const updatedBrackets = JSON.parse(JSON.stringify(t.brackets || []));
    if (!updatedBrackets[bIdx] || !updatedBrackets[bIdx].matches[mIdx]) {
      console.warn('[AdminTournamentCard] Invalid bracket or match index:', bIdx, mIdx);
      return;
    }

    const targetMatch = updatedBrackets[bIdx].matches[mIdx];
    const targetTeamId = slot === 1 ? targetMatch.team1Id : targetMatch.team2Id;
    const targetTeamName = slot === 1 ? targetMatch.t1 : targetMatch.t2;

    // Case 1: Internal Swap between bracket slots
    if (sourceInfo && sourceInfo.type === 'bracket') {
      console.log('[AdminTournamentCard] Executing internal bracket swap');
      const sourceMatch = updatedBrackets[sourceInfo.bIdx].matches[sourceInfo.mIdx];

      // Reset both matches if there's an actual change
      if (sourceMatch.team1Id || sourceMatch.team2Id || targetMatch.team1Id || targetMatch.team2Id) {
        // Target match reset
        targetMatch.winnerId = ''; targetMatch.score = ''; targetMatch.s1 = ''; targetMatch.s2 = ''; targetMatch.s3 = ''; targetMatch.isPublished = false;
        await safeDeleteMatch(`bracket_${t.id}_${bIdx}_${mIdx}`);
        await clearDownstreamWinners(updatedBrackets, bIdx, mIdx);

        // Source match reset
        sourceMatch.winnerId = ''; sourceMatch.score = ''; sourceMatch.s1 = ''; sourceMatch.s2 = ''; sourceMatch.s3 = ''; sourceMatch.isPublished = false;
        await safeDeleteMatch(`bracket_${t.id}_${sourceInfo.bIdx}_${sourceInfo.mIdx}`);
        await clearDownstreamWinners(updatedBrackets, sourceInfo.bIdx, sourceInfo.mIdx);
      }

      // Update Target with Source info
      if (slot === 1) {
        targetMatch.team1Id = teamId;
        targetMatch.t1 = teamName;
      } else {
        targetMatch.team2Id = teamId;
        targetMatch.t2 = teamName;
      }

      // Update Source with Target info (the Swap)
      if (sourceInfo.slot === 1) {
        sourceMatch.team1Id = targetTeamId;
        sourceMatch.t1 = targetTeamName;
      } else {
        sourceMatch.team2Id = targetTeamId;
        sourceMatch.t2 = targetTeamName;
      }
    }
    // Case 2: Simple drop (from Zone or other)
    else {
      console.log('[AdminTournamentCard] Executing external to bracket drop');
      if (slot === 1) {
        if (targetMatch.team1Id !== teamId) {
          // Reset results if team changes
          targetMatch.winnerId = '';
          targetMatch.score = '';
          targetMatch.s1 = '';
          targetMatch.s2 = '';
          targetMatch.s3 = '';
          targetMatch.isPublished = false;
          const matchId = `bracket_${t.id}_${bIdx}_${mIdx}`;
          await safeDeleteMatch(matchId);
          await clearDownstreamWinners(updatedBrackets, bIdx, mIdx);
        }
        targetMatch.team1Id = teamId;
        targetMatch.t1 = teamName;
      } else {
        if (targetMatch.team2Id !== teamId) {
          // Reset results if team changes
          targetMatch.winnerId = '';
          targetMatch.score = '';
          targetMatch.s1 = '';
          targetMatch.s2 = '';
          targetMatch.s3 = '';
          targetMatch.isPublished = false;
          const matchId = `bracket_${t.id}_${bIdx}_${mIdx}`;
          await safeDeleteMatch(matchId);
          await clearDownstreamWinners(updatedBrackets, bIdx, mIdx);
        }
        targetMatch.team2Id = teamId;
        targetMatch.t2 = teamName;
      }
    }

    console.log('[AdminTournamentCard] Submitting bracket update to Firestore');
    // Ensure all undefined values are stripped out, as Firestore rejects them
    const pureBrackets = JSON.parse(JSON.stringify(updatedBrackets));
    
    // Auto-publish logic for Express mode BEFORE updating the tournament doc
    const matchesToPublish: BracketMatch[] = [];
    const publishInfo: { bIdx: number, mIdx: number }[] = [];
    
    if (t.managementMode === 'express') {
      const m = pureBrackets[bIdx]?.matches[mIdx];
      if (m && m.team1Id && m.team2Id && m.team1Id !== 'BYE' && m.team2Id !== 'BYE' && !m.isPublished) {
        m.isPublished = true;
        matchesToPublish.push(m);
        publishInfo.push({ bIdx, mIdx });
      }
      
      if (sourceInfo && sourceInfo.type === 'bracket') {
        const s = pureBrackets[sourceInfo.bIdx]?.matches[sourceInfo.mIdx];
        if (s && s.team1Id && s.team2Id && s.team1Id !== 'BYE' && s.team2Id !== 'BYE' && !s.isPublished) {
          s.isPublished = true;
          matchesToPublish.push(s);
          publishInfo.push({ bIdx: sourceInfo.bIdx, mIdx: sourceInfo.mIdx });
        }
      }
    }

    await updateDoc(doc(db, getCollectionPath('tournaments'), t.id), { brackets: pureBrackets }).then(async () => {
      console.log('[AdminTournamentCard] Update successful');
      
      // Now insert them into the global matches collection
      for (let i = 0; i < matchesToPublish.length; i++) {
        const m = matchesToPublish[i];
        const info = publishInfo[i];
        
        const roundName = pureBrackets[info.bIdx].round;
        const matchId = `bracket_${t.id}_${info.bIdx}_${info.mIdx}`;
        
        const t1Obj = allTeams.find(tm => tm.id === m.team1Id);
        const t2Obj = allTeams.find(tm => tm.id === m.team2Id);
        let pIds: string[] = [];
        if (t1Obj) pIds.push(t1Obj.player1Id, t1Obj.player2Id, t1Obj.player3Id || '');
        if (t2Obj) pIds.push(t2Obj.player1Id, t2Obj.player2Id, t2Obj.player3Id || '');
        pIds = pIds.filter(Boolean);

        const newMatchData = {
          tournamentId: t.id,
          tournamentName: t.name,
          round: roundName,
          team1Id: m.team1Id,
          team2Id: m.team2Id,
          status: 'pending',
          s1: '', s2: '', s3: '', score: '',
          date: m.date || '',
          time: m.time || '',
          organizerId: t.organizerId || auth.currentUser?.uid || '',
          winnerId: null,
          createdAt: Date.now(),
          participantIds: pIds,
          team1Obj: t1Obj || null,
          team2Obj: t2Obj || null
        };
        await setDoc(doc(db, getCollectionPath('matches'), matchId), newMatchData);
      }
    }).catch((er) => {
      console.error('[AdminTournamentCard] Update failed:', er);
    });
  };

  const handleRemoveTeamFromBracket = async (bIdx: number, mIdx: number, slot: 1 | 2) => {
    if (bIdx === 999) {
      const thirdMatch = JSON.parse(JSON.stringify(t.thirdPlaceMatch || { team1Id: '', team2Id: '', s1: '', s2: '', s3: '', score: '', winnerId: '' }));
      if (slot === 1) {
        thirdMatch.team1Id = '';
        thirdMatch.t1 = '';
      } else {
        thirdMatch.team2Id = '';
        thirdMatch.t2 = '';
      }
      thirdMatch.winnerId = ''; thirdMatch.score = ''; thirdMatch.s1 = ''; thirdMatch.s2 = ''; thirdMatch.s3 = ''; thirdMatch.isPublished = false;
      await safeDeleteMatch(`bracket_${t.id}_999_0`);
      await updateDoc(doc(db, getCollectionPath('tournaments'), t.id), { thirdPlaceMatch: thirdMatch });
      return;
    }

    const updatedBrackets = JSON.parse(JSON.stringify(t.brackets || []));
    if (!updatedBrackets[bIdx] || !updatedBrackets[bIdx].matches[mIdx]) return;

    // Reset this match results and published status
    const m = updatedBrackets[bIdx].matches[mIdx];
    m.isPublished = false;
    m.winnerId = '';
    m.score = '';
    m.s1 = '';
    m.s2 = '';
    m.s3 = '';

    if (slot === 1) {
      m.team1Id = '';
      m.t1 = '';
    } else {
      m.team2Id = '';
      m.t2 = '';
    }

    // Delete associated match in global collection if it exists
    const matchId = `bracket_${t.id}_${bIdx}_${mIdx}`;
    await safeDeleteMatch(matchId);

    // Recursively clear recipients in next rounds
    await clearDownstreamWinners(updatedBrackets, bIdx, mIdx);

    await updateDoc(doc(db, getCollectionPath('tournaments'), t.id), { brackets: updatedBrackets });
  };

  const clearDownstreamWinners = async (brackets: any[], bIdx: number, mIdx: number) => {
    let currB = bIdx;
    let currM = mIdx;

    while (currB < brackets.length - 1) {
      const nextB = currB + 1;
      const nextM = Math.floor(currM / 2);
      const isTeam1 = currM % 2 === 0;

      const targetMatch = brackets[nextB].matches[nextM];
      if (!targetMatch) break;

      if (isTeam1) {
        targetMatch.team1Id = '';
        targetMatch.t1 = '';
      } else {
        targetMatch.team2Id = '';
        targetMatch.t2 = '';
      }

      targetMatch.winnerId = '';
      targetMatch.score = '';
      targetMatch.s1 = '';
      targetMatch.s2 = '';
      targetMatch.s3 = '';
      targetMatch.isPublished = false;

      // Delete associated match document for the NEXT match too if it was published
      const nextMatchId = `bracket_${t.id}_${nextB}_${nextM}`;
      await safeDeleteMatch(nextMatchId);

      // If next round is the final, also clear the 3rd place match
      if (nextB === brackets.length - 1 && t.thirdPlaceMatch) {
        const tpMatch = t.thirdPlaceMatch;
        if (isTeam1) {
          tpMatch.team1Id = '';
        } else {
          tpMatch.team2Id = '';
        }
        tpMatch.winnerId = '';
        tpMatch.score = '';
        tpMatch.s1 = '';
        tpMatch.s2 = '';
        tpMatch.s3 = '';
        tpMatch.isPublished = false;
        await safeDeleteMatch(`bracket_${t.id}_999_0`);
        // update tournament with cleaned thirdPlaceMatch
        await updateDoc(doc(db, getCollectionPath('tournaments'), t.id), { thirdPlaceMatch: tpMatch });
      }

      currB = nextB;
      currM = nextM;
    }
  };

  const [showResetConfirm, setShowResetConfirm] = useState(false);

  useEffect(() => {
    if (showResetConfirm) {
      const timer = setTimeout(() => setShowResetConfirm(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [showResetConfirm]);

  const handleResetBrackets = async () => {
    console.log("=> handleResetBrackets starting...");

    if (!t.id) {
      alert('Error: ID del torneo no encontrado.');
      return;
    }

    if (!showResetConfirm) {
      setShowResetConfirm(true);
      return;
    }

    setShowResetConfirm(false);

    // Check if there are results specifically in the BRACKETS
    const hasBracketResults = t.brackets?.some(round =>
      round.matches.some((m: any) => m.winnerId || m.score || (m.s1 && m.s1 !== '0'))
    ) || (t.thirdPlaceMatch && (t.thirdPlaceMatch.winnerId || t.thirdPlaceMatch.score));

    if (hasBracketResults) {
      alert("No se puede reiniciar las llaves porque ya hay resultados registrados en los playoffs. Borra los resultados de las llaves primero.");
      return;
    }

    console.log("=> Proceso de restauración profunda iniciado para:", t.id);

    try {
      // 1. Delete all matches from global collection
      const matchDeletions: Promise<any>[] = [];
      t.brackets?.forEach((bracket: any, bIdx: number) => {
        bracket.matches.forEach((m: any, mIdx: number) => {
          const matchId = `bracket_${t.id}_${bIdx}_${mIdx}`;
          matchDeletions.push(safeDeleteMatch(matchId));
        });
      });
      // Also delete 3rd place match if it exists
      matchDeletions.push(safeDeleteMatch(`bracket_${t.id}_999_0`));

      await Promise.all(matchDeletions);

      // 2. Clear brackets and show size selector
      const tournamentRef = doc(db, getCollectionPath('tournaments'), t.id);
      await updateDoc(tournamentRef, {
        brackets: [],
        thirdPlaceMatch: deleteField()
      });

      setShowAddBracket(true);
      console.log("=> Brackets cleared, returning to size selection.");
    } catch (error) {
      console.error("Error deeply resetting bracket:", error);
      alert("Error al restaurar las llaves.");
    }
  };

  const handlePublishMatch = async (bIdx: number, mIdx: number, match: BracketMatch) => {
    if (!match.team1Id || !match.team2Id) {
      alert('Se necesitan ambos equipos para publicar el partido.');
      return;
    }

    const roundName = bIdx === 999 ? '3er Puesto' : t.brackets[bIdx].round;
    const matchId = `bracket_${t.id}_${bIdx}_${mIdx}`;
    const isBye = match.team1Id === 'BYE' || match.team2Id === 'BYE';
    
    const t1Obj = allTeams.find(tm => tm.id === match.team1Id);
    const t2Obj = allTeams.find(tm => tm.id === match.team2Id);
    let pIds: string[] = [];
    if (t1Obj) pIds.push(t1Obj.player1Id, t1Obj.player2Id, t1Obj.player3Id || '');
    if (t2Obj) pIds.push(t2Obj.player1Id, t2Obj.player2Id, t2Obj.player3Id || '');
    pIds = pIds.filter(Boolean);

    const newMatchData = {
      tournamentId: t.id,
      tournamentName: t.name,
      round: roundName,
      team1Id: match.team1Id,
      team2Id: match.team2Id,
      status: isBye ? 'completed' : 'pending',
      s1: '',
      s2: '',
      s3: '',
      score: isBye ? 'W.O.' : '',
      date: match.date || '',
      time: match.time || '',
      organizerId: t.organizerId || auth.currentUser?.uid || '',
      winnerId: isBye ? match.winnerId : null,
      createdAt: Date.now(),
      participantIds: pIds,
      team1Obj: t1Obj || null,
      team2Obj: t2Obj || null
    };

    try {
      await setDoc(doc(db, getCollectionPath('matches'), matchId), newMatchData);

      if (bIdx === 999) {
        const thirdMatch = JSON.parse(JSON.stringify(t.thirdPlaceMatch));
        thirdMatch.isPublished = true;
        await updateDoc(doc(db, getCollectionPath('tournaments'), t.id), { thirdPlaceMatch: thirdMatch });
      } else {
        const updatedBrackets = JSON.parse(JSON.stringify(t.brackets));
        if (updatedBrackets[bIdx] && updatedBrackets[bIdx].matches[mIdx]) {
          updatedBrackets[bIdx].matches[mIdx].isPublished = true;
          await updateDoc(doc(db, getCollectionPath('tournaments'), t.id), {
            brackets: updatedBrackets
          });
        }
      }
      setEditingMatch(null);
    } catch (error) {
      console.error('Error publishing match:', error);
      alert('Hubo un error al publicar el partido.');
    }
  };

  const handlePublishAllMatches = async () => {
    if (!t.brackets || t.brackets.length === 0) return;
    
    let publishedCount = 0;
    const batch = writeBatch(db);
    const updatedBrackets = JSON.parse(JSON.stringify(t.brackets));

    for (let bIdx = 0; bIdx < updatedBrackets.length; bIdx++) {
      const round = updatedBrackets[bIdx];
      for (let mIdx = 0; mIdx < round.matches.length; mIdx++) {
        const match = round.matches[mIdx];
        if (match.team1Id && match.team2Id && !match.isPublished) {
          const isBye = match.team1Id === 'BYE' || match.team2Id === 'BYE';
          const matchId = `bracket_${t.id}_${bIdx}_${mIdx}`;
          const newMatchData = {
            tournamentId: t.id,
            tournamentName: t.name,
            round: round.round,
            team1Id: match.team1Id,
            team2Id: match.team2Id,
            status: isBye ? 'completed' : 'pending',
            s1: '',
            s2: '',
            s3: '',
            score: isBye ? 'W.O.' : '',
            organizerId: t.organizerId || auth.currentUser?.uid || '',
            winnerId: isBye ? match.winnerId : null,
            createdAt: Date.now(),
          };
          
          batch.set(doc(db, getCollectionPath('matches'), matchId), newMatchData);
          match.isPublished = true;
          publishedCount++;
        }
      }
    }

    if (publishedCount > 0) {
      const tournamentRef = doc(db, getCollectionPath('tournaments'), t.id);
      batch.update(tournamentRef, { brackets: updatedBrackets });
      await batch.commit();
      alert(`¡Se publicaron ${publishedCount} partidos correctamente en la grilla!`);
    } else {
      alert('No se encontraron nuevos partidos listos para publicar (asegúrate de que ambos casilleros de cada partido estén ocupados).');
    }
  };

  const cardRef = React.useRef<HTMLDivElement>(null);

  const prevExpanded = React.useRef(isExpanded);
  useEffect(() => {
    if (isExpanded && !prevExpanded.current && cardRef.current) {
      const el = cardRef.current;
      setTimeout(() => {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
    prevExpanded.current = isExpanded;
  }, [isExpanded]);

  const handleSaveBracketDateTime = async () => {
    if (!editingDateTime) return;
    try {
      const { bIdx, mIdx, match } = editingDateTime;
      const tournamentRef = doc(db, getCollectionPath('tournaments'), t.id);
      
      let isPublishingNow = false;

      if (bIdx === 999) {
        if (t.managementMode === 'express' && (bracketDate || bracketTime) && !match.isPublished && match.team1Id && match.team2Id && match.team1Id !== 'BYE' && match.team2Id !== 'BYE') {
          isPublishingNow = true;
        }
        await updateDoc(tournamentRef, {
          'thirdPlaceMatch.date': bracketDate,
          'thirdPlaceMatch.time': bracketTime,
          ...(isPublishingNow ? { 'thirdPlaceMatch.isPublished': true } : {})
        });
      } else {
        const updatedBrackets = [...(t.brackets || [])];
        if (updatedBrackets[bIdx]?.matches[mIdx]) {
          updatedBrackets[bIdx].matches[mIdx].date = bracketDate;
          updatedBrackets[bIdx].matches[mIdx].time = bracketTime;
          
          if (t.managementMode === 'express' && (bracketDate || bracketTime) && !match.isPublished && match.team1Id && match.team2Id && match.team1Id !== 'BYE' && match.team2Id !== 'BYE') {
            isPublishingNow = true;
            updatedBrackets[bIdx].matches[mIdx].isPublished = true;
          }
          await updateDoc(tournamentRef, { brackets: updatedBrackets });
        }
      }

      const matchId = `bracket_${t.id}_${bIdx}_${mIdx}`;
      if (matches.some((m: any) => m.id === matchId)) {
        const matchRef = doc(db, getCollectionPath('matches'), matchId);
        await updateDoc(matchRef, { date: bracketDate, time: bracketTime });
      } else if (isPublishingNow) {
        const roundName = bIdx === 999 ? '3er Puesto' : t.brackets[bIdx].round;
        const isBye = match.team1Id === 'BYE' || match.team2Id === 'BYE';
        
        const t1Obj = allTeams.find(tm => tm.id === match.team1Id);
        const t2Obj = allTeams.find(tm => tm.id === match.team2Id);
        let pIds: string[] = [];
        if (t1Obj) pIds.push(t1Obj.player1Id, t1Obj.player2Id, t1Obj.player3Id || '');
        if (t2Obj) pIds.push(t2Obj.player1Id, t2Obj.player2Id, t2Obj.player3Id || '');
        pIds = pIds.filter(Boolean);

        const newMatchData = {
          tournamentId: t.id,
          tournamentName: t.name,
          round: roundName,
          team1Id: match.team1Id,
          team2Id: match.team2Id,
          status: isBye ? 'completed' : 'pending',
          s1: '',
          s2: '',
          s3: '',
          score: isBye ? 'W.O.' : '',
          date: bracketDate,
          time: bracketTime,
          organizerId: t.organizerId || auth.currentUser?.uid || '',
          winnerId: isBye ? match.winnerId : null,
          createdAt: Date.now(),
          participantIds: pIds,
          team1Obj: t1Obj || null,
          team2Obj: t2Obj || null
        };
        await setDoc(doc(db, getCollectionPath('matches'), matchId), newMatchData);
      }

      setEditingDateTime(null);
    } catch (error) {
      console.error(error);
      alert('Error guardando fecha y hora.');
    }
  };

  const renderActionButtons = () => (
    <>
      {t.managementMode !== 'express' && t.brackets && t.brackets.length > 0 && t.brackets[0].matches.some((m: any) => m.team1Id && m.team2Id && !m.isPublished) && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            handlePublishAllMatches();
          }}
          className="bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 border border-emerald-500/20 font-black px-2.5 py-1.5 rounded-lg text-[8px] uppercase tracking-widest transition-all shadow-md h-7 flex items-center gap-1.5 group/btn"
        >
          <span>📢 Publicar grilla de partidos de primera ronda</span>
        </button>
      )}

      {/* Finalizar torneo button (antes Finalización manual) - Ahora Azul */}
      {!t.isArchived ? (
        t.status !== 'Lobby' && (
          <button
            disabled={!matches.some((m: any) => m.tournamentId === t.id && (m.winnerId || m.score || m.status === 'completed'))}
            onClick={(e) => {
              e.stopPropagation();
              const hasResults = matches.some((m: any) => m.tournamentId === t.id && (m.winnerId || m.score || m.status === 'completed'));
              if (!hasResults) return;
              handleOpenArchiveModal();
            }}
            className={`font-black px-2.5 py-1.5 rounded-lg text-[8px] uppercase tracking-widest transition-all shadow-md h-7 flex items-center gap-1.5 group/btn ${
              matches.some((m: any) => m.tournamentId === t.id && (m.winnerId || m.score || m.status === 'completed'))
                ? 'bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 border border-blue-500/20'
                : 'bg-slate-500/10 text-slate-500 border border-slate-500/10 opacity-40 cursor-not-allowed'
            }`}
            title={!matches.some((m: any) => m.tournamentId === t.id && (m.winnerId || m.score || m.status === 'completed')) ? 'Se requieren resultados cargados para finalizar' : 'Finalizar torneo'}
          >
            <Trophy size={11} className={matches.some((m: any) => m.tournamentId === t.id && (m.winnerId || m.score || m.status === 'completed')) ? 'group-hover/btn:scale-110 transition-transform text-blue-400' : 'text-slate-500'} />
            <span>Finalizar torneo</span>
          </button>
        )
      ) : null}
      <div className="relative" ref={menuRef}>
        <button
          onClick={(e) => {
            e.stopPropagation();
            setShowActionMenu(!showActionMenu);
            setConfirmDelete(false);
            setShowResetConfirm(false);
          }}
          className="p-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-white/50 hover:text-white transition-all h-7 w-7 flex items-center justify-center border border-white/10"
        >
          <MoreVertical size={14} />
        </button>

        {showActionMenu && (
          <div className={`absolute right-0 top-full mt-2 ${confirmDelete ? 'w-64' : 'w-48'} bg-[#0f172a]/95 backdrop-blur-xl border border-white/10 rounded-xl shadow-2xl py-2 z-50 animate-in zoom-in-95`}>
            {t.brackets && t.brackets.length > 0 && (
              <>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleResetBrackets();
                    if (showResetConfirm) {
                      setShowActionMenu(false);
                    }
                  }}
                  className={`w-full px-4 py-2 flex items-center gap-2 transition-colors text-[10px] font-black uppercase tracking-widest text-left ${
                    showResetConfirm
                      ? 'bg-amber-500/20 text-amber-500 hover:bg-amber-500/30'
                      : 'text-amber-500/70 hover:bg-amber-500/10 hover:text-amber-400'
                  }`}
                >
                  <AlertTriangle size={12} className={showResetConfirm ? "animate-pulse" : ""} />
                  {showResetConfirm ? '¿Confirmar Reset?' : 'Restaurar Llaves'}
                </button>
                <div className="h-px w-full bg-white/5 my-1" />
              </>
            )}

            <button
              onClick={async (e) => {
                e.stopPropagation();
                const url = `${window.location.origin}${window.location.pathname}?t=${t.id}`;
                const text = `¡Mira el torneo "${t.name}" en RPT! 🎾\n\nConsultá la grilla, zonas y resultados aquí:\n${url}`;
                
                if (navigator.share) {
                  try {
                    await navigator.share({
                      title: t.name,
                      text: `¡Mira el torneo "${t.name}" en RPT! 🎾`,
                      url: url
                    });
                  } catch (err) {
                    console.error('Error sharing:', err);
                  }
                } else {
                  window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
                }
                setShowActionMenu(false);
              }}
              className="w-full px-4 py-2 flex items-center gap-2 hover:bg-white/10 text-white/70 hover:text-white transition-colors text-[10px] font-black uppercase tracking-widest text-left"
            >
              <MessageCircle size={12} />
              Compartir
            </button>

            <div className="h-px w-full bg-white/5 my-1" />

            {confirmDelete ? (
              <div className="px-3 py-3 flex flex-col gap-3">
                <div className="text-center space-y-1.5 px-1">
                  <span className="text-[10px] text-red-500 font-black uppercase block leading-tight tracking-widest">⚠️ ACCIÓN IRREVERSIBLE</span>
                  <p className="text-[9px] text-red-400/80 leading-tight font-bold">
                    Se perderá todo el progreso, zonas, llaves y partidos jugados hasta ahora.
                  </p>
                </div>
                <div className="flex gap-2 mt-1">
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDeleteTournament(); setShowActionMenu(false); }}
                    className="flex-[1.5] bg-red-600 text-white font-black py-2 rounded-lg text-[9px] hover:bg-red-500 uppercase tracking-widest text-center shadow-[0_0_15px_rgba(220,38,38,0.4)] transition-all"
                  >
                    ELIMINAR
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); setConfirmDelete(false); }}
                    className="flex-1 bg-white/10 font-black py-2 rounded-lg text-[9px] hover:bg-white/20 uppercase tracking-widest text-center transition-all"
                  >
                    CANCELAR
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setConfirmDelete(true);
                }}
                className="w-full px-4 py-2 flex items-center gap-2 hover:bg-red-500/10 text-red-500/70 hover:text-red-400 transition-colors text-[10px] font-black uppercase tracking-widest text-left"
              >
                <Trash2 size={12} />
                Eliminar Torneo
              </button>
            )}
          </div>
        )}
      </div>
    </>
  );

  const renderLocation = () => {
    const cityName = ALL_CITIES.find(c => c.id === t.locationCity)?.name || t.locationCity || 'Ciudad no especificada';

    if (t.locationMode === 'clubs' && t.locationClubIds && t.locationClubIds.length > 0) {
      const selectedClubs = sedesPublicas.filter(s => t.locationClubIds!.includes(s.uid));
      if (selectedClubs.length > 0) {
        return (
          <div className="flex flex-wrap gap-2 justify-center mb-6">
            {selectedClubs.map(c => {
              const clubCityName = ALL_CITIES.find(city => city.id === c.city)?.name || c.city || 'Ciudad no especificada';
              return (
                <div key={c.uid} className="flex items-center gap-2.5 bg-black/40 rounded-xl p-2 pr-4 border border-white/5">
                  <div className="w-10 h-10 rounded-lg bg-black overflow-hidden shrink-0">
                    {c.photoUrl ? (
                      <img src={c.photoUrl} alt={c.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center theme-primary-bg text-black">
                        <MapPin size={16} />
                      </div>
                    )}
                  </div>
                  <div className="text-left">
                    <div className="text-[7px] font-black uppercase tracking-widest text-white/50 mb-0.5">Dónde se juega</div>
                    <div className="text-xs font-bold text-white leading-tight">{c.name}</div>
                    <div className="text-[9px] opacity-60 leading-none mt-1">{clubCityName}</div>
                  </div>
                </div>
              );
            })}
          </div>
        );
      }
    } else if (t.locationMode === 'tbd') {
      return (
        <div className="flex flex-wrap gap-2 justify-center mb-6">
          <div className="flex items-center gap-2.5 bg-black/40 rounded-xl p-2 pr-4 border border-white/5">
            <div className="w-10 h-10 rounded-lg bg-black overflow-hidden shrink-0">
              <div className="w-full h-full flex items-center justify-center theme-primary-bg text-black">
                <HelpCircle size={16} />
              </div>
            </div>
            <div className="text-left">
              <div className="text-[7px] font-black uppercase tracking-widest text-white/50 mb-0.5">Dónde se juega</div>
              <div className="text-xs font-bold text-white leading-tight">Club: A determinar</div>
              <div className="text-[9px] opacity-60 leading-none mt-1">{cityName}</div>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div
      data-tournament-id={t.id}
      ref={cardRef}
      className={`group border rounded-2xl relative isolate mx-2 transition-all duration-300 ${!isExpanded ? 'hover:scale-[1.01]' : ''} ${isExpanded
        ? 'border-white/50 shadow-[0_4px_10px_rgba(0,0,0,0.35)] z-20'
        : 'border-white/5 hover:border-white/20 shadow-[0_4px_10px_rgba(0,0,0,0.35)]'}`}
      style={{ contentVisibility: 'auto', containIntrinsicSize: isExpanded ? '1000px' : '90px' } as React.CSSProperties}
    >
      {/* Glass Header */}
      <div className={`bg-[#5fa0c4]/25 transition-colors duration-300 group-hover:bg-[#5fa0c4]/35 relative overflow-hidden ${isExpanded ? 'rounded-t-2xl pt-4 px-4 md:pt-5 md:px-5 pb-0' : 'p-4 md:p-5 rounded-2xl'}`}>

      {showBracketHelp && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowBracketHelp(false)}></div>
          <div className="bg-[#0f172a] border border-blue-500/30 w-full max-w-md rounded-[2rem] shadow-[0_0_50px_rgba(0,0,0,0.5)] relative z-10 p-6 animate-in zoom-in-95 duration-200">
            <div className="flex flex-col items-center text-center gap-3 mb-6 mt-2">
              <div className="w-12 h-12 rounded-2xl bg-blue-500/10 flex items-center justify-center text-blue-500">
                <HelpCircle size={24} />
              </div>
              <div>
                <h3 className="text-xl font-black text-white italic uppercase tracking-tight">¿Cómo funciona?</h3>
                <p className="text-[9px] opacity-40 uppercase tracking-widest leading-tight mt-1 max-w-[250px] mx-auto">Generación automática de llaves</p>
              </div>
            </div>

            <div className="space-y-4 mb-8">
              <div className="bg-white/5 border border-white/5 p-4 rounded-xl">
                <h4 className="text-xs font-black text-blue-400 uppercase tracking-widest mb-1.5 flex items-center gap-2"><span className="w-4 h-4 rounded bg-blue-500/20 flex items-center justify-center text-[10px]">1</span> Tabla General</h4>
                <p className="text-[10px] text-white/60 leading-relaxed">Se ordenan todos los clasificados. Primero los mejores 1eros de cada zona, luego los 2dos, etc. Los empates se definen por <strong className="text-white">Puntos</strong> &rarr; <strong className="text-white">Dif. Sets</strong> &rarr; <strong className="text-white">Dif. Juegos</strong>.</p>
              </div>
              <div className="bg-white/5 border border-white/5 p-4 rounded-xl">
                <h4 className="text-xs font-black text-blue-400 uppercase tracking-widest mb-1.5 flex items-center gap-2"><span className="w-4 h-4 rounded bg-blue-500/20 flex items-center justify-center text-[10px]">2</span> Cruce Matemático</h4>
                <p className="text-[10px] text-white/60 leading-relaxed">Siguiendo la siembra deportiva, el 1° juega contra el último, el 2° contra el penúltimo, etc. Asegurando cruces justos y equitativos.</p>
              </div>
              <div className="bg-white/5 border border-white/5 p-4 rounded-xl border-l-2 border-l-amber-500">
                <h4 className="text-xs font-black text-amber-500 uppercase tracking-widest mb-1.5 flex items-center gap-2"><span className="w-4 h-4 rounded bg-amber-500/20 flex items-center justify-center text-[10px] text-amber-500">3</span> Regla Anti-Repetición</h4>
                <p className="text-[10px] text-white/60 leading-relaxed">Si dos equipos de la misma zona quedan emparejados en primera ronda, el sistema inteligentemente intercambia posiciones para evitarlo.</p>
              </div>
            </div>

            <div className="flex justify-center">
              <button
                onClick={() => setShowBracketHelp(false)}
                className="w-full bg-blue-500 hover:bg-blue-400 text-white py-3 rounded-xl font-black text-[10px] uppercase tracking-[0.2em] transition-all shadow-lg active:scale-[0.98]"
              >
                Entendido
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {showArchiveModal && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setShowArchiveModal(false)}></div>
          <div className="bg-slate-800 border border-white/10 w-full max-w-md rounded-[2rem] shadow-2xl relative z-10 p-6 animate-in zoom-in-95 duration-200">
            <div className="flex flex-col items-center text-center gap-3 mb-6 mt-2">
              <div className="w-12 h-12 rounded-2xl bg-yellow-500/10 flex items-center justify-center text-yellow-500">
                <Trophy size={24} />
              </div>
              <div>
                <h3 className="text-xl font-black text-white italic uppercase tracking-tight">Finalización del torneo</h3>
                <p className="text-[9px] opacity-40 uppercase tracking-widest leading-tight mt-1 max-w-[250px] mx-auto">Define el podio y se notificarán los resultados a las parejas ganadoras</p>
              </div>
            </div>

            <div className="space-y-2.5 max-h-[40vh] overflow-y-auto no-scrollbar pr-1 mb-6">
              {modalRankings.map((rank, idx) => {
                return (
                  <div key={idx} className="bg-white/5 border border-white/5 p-3 rounded-xl flex items-center gap-3 group/rank relative">
                    <div className={`w-8 h-8 shrink-0 flex items-center justify-center rounded-lg font-black text-xs italic ${rank.rank === 1 ? 'bg-yellow-500 text-black shadow-[0_0_10px_rgba(234,179,8,0.3)]' :
                        rank.rank === 2 ? 'bg-slate-400 text-black' :
                          rank.rank === 3 ? 'bg-yellow-700 text-white shadow-[0_0_10px_rgba(161,98,7,0.2)]' :
                            'bg-white/10 text-white'
                      }`}>
                      {rank.rank === 4 ? 'SF' : `${rank.rank}°`}
                    </div>

                    <div className="flex-1 min-w-0 flex flex-col gap-1.5">
                      <SearchableTeamSelect
                        value={rank.teamId}
                        options={allTeams.filter(team =>
                          t.registeredTeamIds?.includes(team.id) &&
                          (!modalRankings.some(r => r.teamId === team.id) || rank.teamId === team.id)
                        )}
                        onChange={(val) => {
                          const newRankings = [...modalRankings];
                          newRankings[idx].teamId = val;
                          setModalRankings(newRankings);
                        }}
                        placeholder={rank.rank === 1 ? 'Campeón...' : rank.rank === 2 ? 'Finalista...' : (rank.rank === 4 ? 'Semifinalista...' : `${rank.rank}° Puesto...`)}
                        className="w-full bg-black/60 border border-white/10 rounded-lg p-2.5 text-xs font-bold text-white placeholder-white/30 outline-none focus:border-yellow-500/50 transition-all"
                      />
                    </div>

                    <div className="shrink-0 flex items-center gap-1 bg-yellow-500/10 border border-yellow-500/20 rounded-md px-2 py-1 transition-all focus-within:border-yellow-500/50 focus-within:bg-yellow-500/20">
                      <span className="text-[10px] font-black text-yellow-500 uppercase">+</span>
                      <input
                        type="number"
                        min="0"
                        value={rank.points}
                        onChange={(e) => {
                          const val = parseInt(e.target.value) || 0;
                          const newRankings = [...modalRankings];
                          newRankings[idx].points = val;
                          setModalRankings(newRankings);
                        }}
                        className="w-8 bg-transparent text-[10px] font-black text-yellow-500 outline-none text-center no-spinners"
                      />
                      <span className="text-[10px] font-black text-yellow-500 uppercase tracking-widest">PTS</span>
                    </div>

                    {modalRankings.length > 3 && (
                      <button
                        onClick={() => {
                          const newRankings = modalRankings.filter((_, i) => i !== idx);
                          setModalRankings(newRankings);
                        }}
                        className="p-1.5 text-red-500/50 hover:text-red-500 transition-colors shrink-0"
                      >
                        <XIcon size={14} />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="space-y-3">
              <div className="flex justify-center mb-2">
                <button
                  onClick={toggleThirdPlace}
                  className={`px-4 py-1.5 border rounded-lg text-[8px] uppercase font-black tracking-widest transition-all flex items-center justify-center gap-1.5 ${
                    isThirdPlaceActive 
                      ? 'border-white/30 bg-white/10 text-white' 
                      : 'border-white/10 text-white/40 hover:border-white/20 hover:text-white/80 hover:bg-white/5'
                  }`}
                >
                  <Trophy size={10} /> {isThirdPlaceActive ? 'Desactivar 3er Puesto' : 'Activar 3er Puesto'}
                </button>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setShowArchiveModal(false)}
                  className="flex-1 py-3 text-[9px] font-black uppercase tracking-widest text-white/40 hover:text-white transition-all"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleArchive}
                  className="flex-[2] bg-yellow-500 hover:bg-yellow-400 text-black py-3 rounded-xl font-black text-[9px] uppercase tracking-[0.2em] transition-all shadow-lg active:scale-[0.98]"
                >
                  Confirmar
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Status Tags */}
      <div className="absolute top-0 right-0 flex z-30">
        {isLobby && (
          <motion.span
            animate={{ opacity: [1, 0.4, 1] }}
            transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
            className="flex items-center gap-1 px-2.5 py-0.5 rounded-bl-xl rounded-tr-2xl bg-lime-500/20 text-lime-400 text-[7px] md:text-[8px] font-black tracking-widest border-b border-l border-lime-500/30 shadow-sm backdrop-blur-md"
          >
            <div className="w-1.5 h-1.5 rounded-full bg-lime-500/80 shadow-[0_0_8px_rgba(132,204,22,0.3)] animate-pulse" />
            INSCRIPCIÓN
          </motion.span>
        )}
        {isActiveStatus && (
          <span className="flex items-center gap-1 px-2.5 py-0.5 rounded-bl-xl rounded-tr-2xl bg-white/10 text-white/90 text-[7px] md:text-[8px] font-black tracking-widest border-b border-l border-white/20 shadow-sm backdrop-blur-md">
            <motion.div
              animate={{ opacity: [1, 0.4, 1] }}
              transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
              className="w-1.5 h-1.5 rounded-full bg-lime-500 shadow-[0_0_8px_rgba(132,204,22,0.4)]"
            />
            {t.status?.toUpperCase()}
          </span>
        )}
      </div>

      <div
        className={`relative z-10 flex flex-col md:flex-row md:items-center justify-between cursor-pointer group gap-3 md:gap-4 transition-all ${isExpanded ? 'mb-6 pb-4 border-b border-white/10' : ''}`}
        onClick={onToggle}
      >
        <div className="flex items-start md:items-center gap-4 flex-1 min-w-0 w-full">
          <div className="flex flex-col items-center justify-center gap-1.5 shrink-0 pr-4 md:pr-5 border-r border-white/10 pt-1 md:pt-0">
            <span className="text-[7px] md:text-[8px] font-black uppercase tracking-[0.2em] text-white/40">Organiza:</span>
            
            {logoUrl && (logoUrl !== config.logoUrl || isSuperAdminOrganizer) ? (
              <div className="w-16 h-16 md:w-24 md:h-24 flex items-center justify-center">
                <img 
                  src={processedLogo || logoUrl}
                  alt={organizerName}
                  className="w-full h-full object-contain transition-transform group-hover:scale-105 duration-300"
                  referrerPolicy="no-referrer"
                />
              </div>
            ) : (
              <div className="w-16 h-16 md:w-24 md:h-24 rounded-xl flex items-center justify-center theme-primary-bg text-black shadow-lg transition-transform group-hover:scale-105 duration-300">
                <Trophy size={24} />
              </div>
            )}
            
            <span className="text-[8px] md:text-[9px] font-black uppercase tracking-wider text-theme-primary truncate w-full text-center max-w-[80px] md:max-w-[100px]">
              {organizerName}
            </span>
          </div>

          <div className="flex flex-col min-w-0 gap-1.5 flex-1 relative pr-8 md:pr-0">
            <h3 className="text-lg md:text-xl font-black theme-primary-text theme-font-secondary uppercase italic leading-none flex flex-col items-start gap-1.5 pt-1 md:pt-3">
              <span className="break-words w-full [text-wrap:balance] line-clamp-3">{t.name}</span>
            </h3>

            {/* 2, 3, 4, 5. Combined Metadata Row */}
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2 mt-1 md:mt-0">
              {/* 2. Category */}
              {t.category && (
                <span className="px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-tighter border border-white/30 text-white shrink-0">
                  {t.category.toLowerCase() === 'libre' ? 'CAT. LIBRE' : t.category}
                </span>
              )}

              {/* 3. Gender */}
              {t.gender && (
                <div className={`flex items-center gap-1 text-[9px] font-black uppercase tracking-widest shrink-0 ${t.gender?.toLowerCase() === 'masculino' ? 'text-blue-400' :
                  t.gender?.toLowerCase() === 'femenino' ? 'text-violet-400' :
                  (t.gender?.toLowerCase() === 'sin_restriccion' || t.gender?.toLowerCase() === 'sin restricción') ? 'text-blue-400' :
                    'text-white'
                  }`}>
                  {t.gender?.toLowerCase() === 'masculino' && <Mars size={10} />}
                  {t.gender?.toLowerCase() === 'femenino' && <Venus size={10} />}
                  {(!['masculino', 'femenino', 'sin_restriccion', 'sin restricción'].includes(t.gender?.toLowerCase() || '')) && (
                    <div className="flex -space-x-1 text-yellow-400">
                      <Mars size={10} />
                      <Venus size={10} />
                    </div>
                  )}
                  {t.gender?.toLowerCase() === 'masculino' ? 'Caballeros' :
                    t.gender?.toLowerCase() === 'femenino' ? 'Damas' : 
                    (t.gender?.toLowerCase() === 'sin_restriccion' || t.gender?.toLowerCase() === 'sin restricción') ? 'Género Libre' : 'Mixto'}
                </div>
              )}

              {/* 4. Registered Players (Cupos) */}
              <div className="flex items-center gap-1.5 opacity-70 shrink-0">
                <Users size={12} className="theme-primary-text" />
                <span className="text-[10px] font-bold uppercase tracking-tight">
                  {registeredCount}/{t.maxTeams || 16} Cupos
                </span>
              </div>

              {/* 5. Date (Shortened format) */}
              <div className="flex items-center gap-1.5 opacity-70 shrink-0">
                <CalendarDays size={12} className="theme-primary-text" />
                <span className="text-[10px] font-bold uppercase tracking-tight">
                  {t.startedAt
                    ? `Inicio: ${(() => {
                      const d = new Date(t.startedAt);
                      return `${d.getDate()}-${d.getMonth() + 1}-${d.getFullYear().toString().slice(-2)}`;
                    })()}`
                    : t.startDate ? `Fecha (Estimada): ${(() => {
                      const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
                      if (dateRegex.test(t.startDate)) {
                        const [y, m, d] = t.startDate.split('-');
                        return `${parseInt(d)}-${parseInt(m)}-${y.slice(-2)}`;
                      }
                      return t.startDate;
                    })()}` : 'A definir'}
                </span>
              </div>
            </div>

            <div className="md:hidden mt-2">
              <div className="scale-90 origin-left">
                <ChampionBadge champion={champion || null} />
              </div>
            </div>

            <motion.div 
              animate={{ rotate: isExpanded ? 0 : -90 }}
              transition={{ duration: 0.3 }}
              className="absolute top-1 md:top-auto md:relative right-0 flex items-center justify-center text-white/40 group-hover:text-white/70 transition-colors md:hidden"
            >
              <ChevronDown size={20} />
            </motion.div>
          </div>
        </div>

        <div className="hidden md:flex items-center gap-2 shrink-0">
          <div className="scale-75 origin-right">
            <ChampionBadge champion={champion || null} />
          </div>

          <motion.div 
            animate={{ rotate: isExpanded ? 0 : -90 }}
            transition={{ duration: 0.3 }}
            className="flex items-center justify-center text-white/40 group-hover:text-white/70 transition-colors ml-2"
          >
            <ChevronDown size={20} />
          </motion.div>
        </div>
        </div>
      </div>

      <div className={`overflow-hidden transition-[max-height] duration-300 ease-in-out ${isExpanded ? 'max-h-[20000px]' : 'max-h-0'}`}>
        <div className="bg-slate-900/50 px-4 md:px-5 pb-4 md:pb-5 pt-4 rounded-b-2xl border-t border-white/10">
          {isLobby ? (
            <div className="flex flex-col-reverse lg:flex-row lg:items-center justify-center gap-4 lg:gap-8 mb-6 lg:mb-2 max-w-5xl mx-auto">
              {/* Lobby actions area */}
              <div className="flex items-center gap-2 w-full lg:w-[400px] max-w-md mx-auto lg:mx-0 h-10 shrink-0">
                <AnimatePresence mode="wait">
                {!lobbyShowExitConfirm ? (
                  <motion.div className="flex items-center gap-2 w-full h-full" key="actions">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (lobbyIsEditingInfo) {
                          if (lobbyHasChanges) {
                            setLobbyShowExitConfirm(true);
                          } else {
                            setLobbyIsEditingInfo(false);
                          }
                        } else {
                          setLobbyIsEditingInfo(true);
                        }
                      }}
                      className={`flex-1 flex items-center justify-center gap-2 h-full rounded-xl font-black uppercase text-[10px] tracking-widest transition-all border shadow-md ${
                        lobbyIsEditingInfo 
                          ? 'theme-primary-bg text-black border-theme-primary' 
                          : 'bg-white/5 hover:bg-white/10 text-white/60 hover:text-white border-white/10'
                      }`}
                    >
                      {lobbyIsEditingInfo ? <XIcon size={13} /> : <Edit size={13} />}
                      {lobbyIsEditingInfo ? 'Cerrar Edición' : 'Editar Torneo'}
                    </button>

                    {!lobbyIsEditingInfo && (
                      <>
                        <button
                          onClick={async (e) => {
                            e.stopPropagation();
                            const url = `${window.location.origin}${window.location.pathname}?t=${t.id}`;
                            const text = `¡Mira el torneo "${t.name}" en RPT! 🎾\n\nConsultá la grilla, zonas y resultados aquí:\n${url}`;
                            
                            if (navigator.share) {
                              try {
                                await navigator.share({
                                  title: t.name,
                                  text: `¡Mira el torneo "${t.name}" en RPT! 🎾`,
                                  url: url
                                });
                              } catch (err) {
                                console.error('Error sharing:', err);
                              }
                            } else {
                              window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
                            }
                          }}
                          title="Compartir"
                          className="w-12 h-full flex items-center justify-center shrink-0 rounded-xl bg-white/5 hover:bg-white/10 text-white/70 hover:text-white transition-all border border-white/10 shadow-md active:scale-95"
                        >
                          <Share2 size={15} />
                        </button>

                        {confirmDelete ? (
                          <div className="flex gap-1.5 items-center justify-center bg-red-500/10 h-full px-2 rounded-xl border border-red-500/20 animate-in zoom-in-95 shrink-0">
                            <span className="text-[8px] text-red-300 font-bold uppercase">¿Borrar?</span>
                            <button
                              onClick={(e) => { e.stopPropagation(); handleDeleteTournament(); }}
                              className="bg-red-500 text-white font-black px-1.5 py-1 rounded text-[8px] hover:bg-red-600 uppercase"
                            >
                              Sí
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); setConfirmDelete(false); }}
                              className="bg-white/10 font-black px-1.5 py-1 rounded text-[8px] hover:bg-white/20 uppercase"
                            >
                              No
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setConfirmDelete(true);
                            }}
                            title="Eliminar Torneo"
                            className="w-12 h-full flex items-center justify-center shrink-0 rounded-xl bg-white/5 hover:bg-white/10 text-red-500/80 hover:text-red-400 transition-all border border-white/10 shadow-md active:scale-95"
                          >
                            <Trash2 size={14} />
                          </button>
                        )}
                      </>
                    )}
                  </motion.div>
                ) : (
                  <motion.div
                    key="confirm-area"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    className="flex items-center justify-between gap-4 bg-slate-900 border border-amber-500/30 p-2 pl-4 rounded-xl shadow-md w-full h-full"
                  >
                    <div className="flex flex-col text-left">
                      <span className="text-[9px] font-black uppercase theme-primary-text tracking-widest leading-none">¿Guardar?</span>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <button
                        onClick={async (e) => {
                          e.stopPropagation();
                          if (lobbySaveTriggerRef.current) {
                            await lobbySaveTriggerRef.current();
                          }
                          setLobbyShowExitConfirm(false);
                        }}
                        className="px-3 py-1 theme-primary-bg text-black font-black rounded-lg uppercase text-[9px] tracking-widest hover:brightness-110 active:scale-95 transition-all"
                      >
                        Sí
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (lobbyDiscardTriggerRef.current) {
                            lobbyDiscardTriggerRef.current();
                          }
                          setLobbyIsEditingInfo(false);
                          setLobbyShowExitConfirm(false);
                        }}
                        className="px-3 py-1 bg-red-500/10 text-red-500 font-black rounded-lg uppercase text-[9px] tracking-widest hover:bg-red-500/20 active:scale-95 transition-all"
                      >
                        No
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setLobbyShowExitConfirm(false);
                        }}
                        className="p-1.5 text-white/20 hover:text-white transition-colors"
                      >
                        <XIcon size={12} />
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            
            <div className="shrink-0">
              <div className="lg:-mb-6 flex justify-center">
                {renderLocation()}
              </div>
            </div>
          </div>
          ) : (
            /* Standard Admin Control Panel Card Block - rendered only when not in Lobby/inscription process */
            <>
              {renderLocation()}
              {(!t.brackets || t.brackets.length === 0) && (
              <div className="relative flex flex-col items-center justify-center p-5 rounded-2xl bg-white/[0.02] border border-white/5 mb-6 z-20">
                {/* Botones de acción rápidos - Centrados y compactos */}
                <div className="flex flex-wrap items-center justify-center gap-2.5 w-full">
                  {/* Volver al lobby button */}
                  {!t.isArchived && (t.status === 'En Juego' || t.status === 'Fase de Grupos' || t.status === 'Eliminatorias') && (
                    <div className="flex items-center gap-1.5">
                      {showLobbyConfirm ? (
                        <div className="flex gap-2 items-center bg-blue-500/10 p-1 rounded-lg border border-blue-500/20 animate-in zoom-in-95 h-7">
                          <span className="text-[8px] text-blue-300 font-bold uppercase ml-1">¿Borrar grupos y volver?</span>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleBackToLobby(); }}
                            className="bg-blue-500 text-white font-black px-2 py-1 rounded text-[8px] hover:bg-blue-600 uppercase"
                          >
                            Sí
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); setShowLobbyConfirm(false); }}
                            className="bg-white/10 font-black px-2 py-1 rounded text-[8px] hover:bg-white/20 uppercase"
                          >
                            No
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            const hasPlayedMatches = matches.some(m => m.tournamentId === t.id && (m.winnerId || m.score || m.status === 'completed'));
                            if (hasPlayedMatches) {
                              alert("No se puede volver al lobby porque ya hay resultados registrados.");
                            } else {
                              setShowLobbyConfirm(true);
                            }
                          }}
                          disabled={matches.some(m => m.tournamentId === t.id && (m.winnerId || m.score || m.status === 'completed'))}
                          className={`font-black px-2.5 py-1.5 rounded-lg text-[8px] uppercase tracking-widest transition-all shadow-md h-7 flex items-center gap-1.5 ${!matches.some(m => m.tournamentId === t.id && (m.winnerId || m.score || m.status === 'completed'))
                              ? 'bg-white/5 text-white hover:bg-white/10 border border-white/20'
                              : 'bg-slate-500/10 text-slate-500 border border-slate-500/10 opacity-40 cursor-not-allowed'
                            }`}
                          title="Volver al Lobby (Inscripciones)"
                        >
                          <RotateCcw size={10} />
                          <span>Volver al Lobby</span>
                        </button>
                      )}
                    </div>
                  )}

                  {renderActionButtons()}
                </div>
              </div>
            )}
            </>
          )}
          {isLobby ? (
            <>
              <AdminTournamentLobby
                t={t}
                sedeName={organizerName}
                allTeams={allTeams}
                players={players}
                db={db}
                getCollectionPath={getCollectionPath}
                config={config}
                rankingConfig={rankingConfig}
                sedesPublicas={sedesPublicas}
                isEditingInfo={lobbyIsEditingInfo}
                setIsEditingInfo={setLobbyIsEditingInfo}
                showExitConfirm={lobbyShowExitConfirm}
                setShowExitConfirm={setLobbyShowExitConfirm}
                onHasChangesChange={setLobbyHasChanges}
                saveTriggerRef={lobbySaveTriggerRef}
                discardTriggerRef={lobbyDiscardTriggerRef}
              />
              {lobbyIsEditingInfo && (
                <div className="flex items-center gap-3 w-full max-w-md mx-auto justify-center mt-6">
                  <button
                    onClick={async (e) => {
                      e.stopPropagation();
                      const url = `${window.location.origin}${window.location.pathname}?t=${t.id}`;
                      const text = `¡Mira el torneo "${t.name}" en RPT! 🎾\n\nConsultá la grilla, zonas y resultados aquí:\n${url}`;
                      
                      if (navigator.share) {
                        try {
                          await navigator.share({
                            title: t.name,
                            text: `¡Mira el torneo "${t.name}" en RPT! 🎾`,
                            url: url
                          });
                        } catch (err) {
                          console.error('Error sharing:', err);
                        }
                      } else {
                        window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
                      }
                    }}
                    title="Compartir por WhatsApp"
                    className="flex-1 px-4 py-2 flex items-center justify-center gap-1.5 rounded-xl bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-500 font-black text-[9px] uppercase tracking-widest transition-all border border-emerald-500/20 shadow-md h-8.5 active:scale-95"
                  >
                    <MessageCircle size={12} />
                    <span>Compartir</span>
                  </button>

                  {confirmDelete ? (
                    <div className="flex-1 flex gap-2 items-center justify-center bg-red-500/10 p-1 rounded-xl border border-red-500/20 animate-in zoom-in-95 h-8.5">
                      <span className="text-[8px] text-red-300 font-bold uppercase ml-1">¿Borrar?</span>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDeleteTournament(); }}
                        className="bg-red-500 text-white font-black px-2 py-1 rounded text-[8px] hover:bg-red-600 uppercase"
                      >
                        Sí
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); setConfirmDelete(false); }}
                        className="bg-white/10 font-black px-2 py-1 rounded text-[8px] hover:bg-white/20 uppercase"
                      >
                        No
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setConfirmDelete(true);
                      }}
                      className="flex-1 px-3 py-1.5 flex items-center justify-center gap-1 rounded-xl bg-red-500/10 hover:bg-red-500/20 hover:text-red-400 text-red-500 border border-red-500/20 font-black text-[7.5px] uppercase tracking-widest transition-all shadow-md h-8.5 active:scale-95"
                    >
                      <Trash2 size={11} />
                      <span>Eliminar Torneo</span>
                    </button>
                  )}
                </div>
              )}
            </>
          ) : (
            <>




              {showRulesEdit && (
                <div className="bg-black/40 p-4 rounded-xl border theme-primary-border mb-6 space-y-4">
                  <h4 className="font-bold theme-primary-text text-sm uppercase">Editar Reglamento</h4>
                  <textarea
                    value={rulesText}
                    onChange={(e) => setRulesText(e.target.value)}
                    placeholder="Escribe el reglamento aquí (Opcional)..."
                    className="w-full bg-black/40 border border-white/10 rounded-lg p-3 text-sm outline-none h-24"
                  />

                  <div className="space-y-2 p-3 bg-black/40 rounded-lg border border-white/5">
                    <label className="text-xs opacity-60 font-bold block">Subir archivo PDF</label>
                    <div className="flex items-center gap-2">
                      <input type="file" accept=".pdf" onChange={handlePdfUpload} className="text-xs text-white placeholder-white/30" disabled={uploadingPdf} />
                      {uploadingPdf && <span className="text-xs theme-primary-text animate-pulse font-bold">Cargando PDF...</span>}
                    </div>
                    <div className="mt-2 pt-2 border-t border-white/5">
                      <label className="text-xs opacity-60 font-bold block mb-1">O pega un enlace externo directamente (Drive, Dropbox, etc.)</label>
                      <input
                        type="text"
                        value={rulesPdfUrl}
                        onChange={(e) => setRulesPdfUrl(e.target.value)}
                        placeholder="URL del PDF (Ej: https://...)"
                        className="w-full bg-black/60 border border-white/10 rounded-lg p-2 text-sm outline-none"
                      />
                    </div>
                  </div>

                  <div className="flex justify-end gap-2">
                    <button onClick={() => setShowRulesEdit(false)} className="px-4 py-2 text-sm opacity-60 font-bold">
                      Cancelar
                    </button>
                    <button onClick={saveRules} className="px-4 py-2 text-sm theme-primary-bg text-black font-black rounded-lg">
                      Guardar
                    </button>
                  </div>
                </div>
              )}



              <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 relative z-10">
                {(t.zones || []).map((z, zIdx) => (
                  <AdminTournamentZone
                    key={zIdx}
                    tId={t.id}
                    tZones={t.zones}
                    zone={z}
                    zIdx={zIdx}
                    allTeams={allTeams}
                    players={players}
                    db={db}
                    getCollectionPath={getCollectionPath}
                    hasPlayoffs={t.hasPlayoffs}
                    qualifiersPerZone={t.qualifiersPerZone}
                    onDropGlobal={handleDropTeamToBracket}
                  />
                ))}
              </div>

              {/* Advance to Elimination Button Below Zones */}
              {isExpanded && t.status !== 'Lobby' && !t.isArchived && (!t.brackets || t.brackets.length === 0) && (() => {
                const pendingGroupMatches = matches.filter(m => m.tournamentId === t.id && (m.round.includes('Zona') || m.round.includes('Grupo')) && !m.winnerId && m.status !== 'completed').length;
                return (
                <div className="mt-8 flex flex-col items-center justify-center w-full gap-6">
                  {!showAddBracket && (
                      <button
                      onClick={() => setShowAddBracket(true)}
                      className="bg-blue-500 text-white hover:bg-blue-400 font-black px-8 py-4 rounded-2xl text-xs uppercase tracking-[0.2em] transition-all shadow-[0_0_30px_rgba(59,130,246,0.3)] flex items-center gap-3 group animate-in slide-in-from-bottom-4 duration-500"
                    >
                      <ChevronsRight size={18} className="animate-nudge-right" />
                      Avanzar a la fase de eliminación
                    </button>
                  )}

                  {showAddBracket && (
                    <div className="flex flex-col gap-4 w-full max-w-2xl bg-[#1e293b] p-4 md:p-5 rounded-2xl border theme-accent-border shadow-2xl animate-in slide-in-from-top-4 duration-500 mx-auto">
                      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3">
                        <div>
                          <h3 className="text-sm font-black uppercase tracking-widest text-white flex items-center gap-2">
                            <Trophy size={16} className="text-theme-accent" /> Configuración de Llaves
                          </h3>
                          <p className="text-[10px] text-white/40 uppercase tracking-widest mt-1">Elige cómo deseas iniciar los playoffs</p>
                        </div>
                      </div>

                      <div className="flex flex-col md:flex-row gap-3 items-center">
                        <div className="flex-1 w-full">
                          <div className="flex items-center gap-1.5 mb-1.5">
                            <label className="text-[10px] uppercase font-bold tracking-widest opacity-80 text-white m-0 leading-none">Total parejas clasificadas</label>
                            <div className="group/tt relative flex items-center">
                              <HelpCircle size={12} className="text-white/40 hover:text-white/80 cursor-help transition-colors" />
                              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 bg-[#0f172a] text-white/80 text-[10px] p-2.5 rounded-lg border border-white/10 opacity-0 invisible group-hover/tt:opacity-100 group-hover/tt:visible transition-all z-50 text-center font-normal normal-case shadow-xl leading-relaxed pointer-events-none">
                                Cuántas parejas clasifican de la zona de grupos
                                <div className="absolute top-full left-1/2 -translate-x-1/2 border-[5px] border-transparent border-t-white/10"></div>
                                <div className="absolute top-full left-1/2 -translate-x-1/2 border-[5px] border-transparent border-t-[#0f172a] -mt-[1px]"></div>
                              </div>
                            </div>
                          </div>
                          <select
                            value={numBracketTeams}
                            onChange={(e) => setNumBracketTeams(e.target.value)}
                            className="w-full bg-slate-800/80 font-bold p-2.5 rounded-xl outline-none border border-white/10 focus:border-blue-500/50 transition-colors text-xs"
                          >
                            <option value="16">16 Parejas (Inicia en Octavos)</option>
                            <option value="8">8 Parejas (Inicia en Cuartos)</option>
                            <option value="4">4 Parejas (Inicia en Semis)</option>
                            <option value="2">2 Parejas (Final Directa)</option>
                          </select>
                        </div>
                        {t.managementMode !== 'express' && (
                          <>
                            <div className="flex-1 w-full">
                              <div className="flex items-center gap-1.5 mb-1.5">
                                <label className="text-[10px] uppercase font-bold tracking-widest opacity-80 text-white m-0 leading-none">Inicio de fase</label>
                                <div className="group/tt relative flex items-center">
                                  <HelpCircle size={12} className="text-white/40 hover:text-white/80 cursor-help transition-colors" />
                                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-56 bg-[#0f172a] text-white/80 text-[10px] p-2.5 rounded-lg border border-white/10 opacity-0 invisible group-hover/tt:opacity-100 group-hover/tt:visible transition-all z-50 text-center font-normal normal-case shadow-xl leading-relaxed pointer-events-none">
                                    Determinará el día en el que comienzan las grillas según la duración de cada ronda
                                    <div className="absolute top-full left-1/2 -translate-x-1/2 border-[5px] border-transparent border-t-white/10"></div>
                                    <div className="absolute top-full left-1/2 -translate-x-1/2 border-[5px] border-transparent border-t-[#0f172a] -mt-[1px]"></div>
                                  </div>
                                </div>
                              </div>
                              <input
                                type="date"
                                value={eliminationStartDate}
                                onChange={(e) => setEliminationStartDate(e.target.value)}
                                className="w-full bg-slate-800/80 font-bold p-2.5 rounded-xl outline-none border border-white/10 focus:border-blue-500/50 transition-colors text-xs [color-scheme:dark]"
                              />
                            </div>
                            <div className="flex-1 w-full">
                              <div className="flex items-center gap-1.5 mb-1.5">
                                <label className="text-[10px] uppercase font-bold tracking-widest opacity-80 text-white m-0 leading-none">Días por ronda</label>
                                <div className="group/tt relative flex items-center">
                                  <HelpCircle size={12} className="text-white/40 hover:text-white/80 cursor-help transition-colors" />
                                  <div className="absolute bottom-full right-0 md:left-1/2 md:-translate-x-1/2 mb-2 w-48 bg-[#0f172a] text-white/80 text-[10px] p-2.5 rounded-lg border border-white/10 opacity-0 invisible group-hover/tt:opacity-100 group-hover/tt:visible transition-all z-50 text-center font-normal normal-case shadow-xl leading-relaxed pointer-events-none">
                                    Cuántos días tienen los jugadores para jugar cada fase
                                    <div className="absolute top-full right-2 md:left-1/2 md:-translate-x-1/2 border-[5px] border-transparent border-t-white/10"></div>
                                    <div className="absolute top-full right-2 md:left-1/2 md:-translate-x-1/2 border-[5px] border-transparent border-t-[#0f172a] -mt-[1px]"></div>
                                  </div>
                                </div>
                              </div>
                              <input
                                type="number"
                                min="1"
                                max="30"
                                value={eliminationDaysPerRound}
                                onChange={(e) => setEliminationDaysPerRound(Number(e.target.value))}
                                className="w-full bg-slate-800/80 font-bold p-2.5 rounded-xl outline-none border border-white/10 focus:border-blue-500/50 transition-colors text-xs"
                              />
                            </div>
                          </>
                        )}
                      </div>

                      <div className="border-t border-white/10 pt-4 mt-2">
                        <label className="text-[10px] uppercase font-bold tracking-widest text-white/70 block mb-3">Armado de llaves de eliminación</label>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div
                            onClick={() => setBracketGenMode('manual')}
                            className={`flex flex-col text-left p-3 rounded-xl border transition-all cursor-pointer ${bracketGenMode === 'manual' ? 'bg-white/10 border-white/50 shadow-md' : 'bg-black/20 border-white/5 hover:border-white/20'}`}
                          >
                            <span className={`text-sm font-black uppercase tracking-widest mb-1 ${bracketGenMode === 'manual' ? 'text-white' : 'text-white/60'}`}>Manual</span>
                            <span className="text-[10px] text-white/50 leading-relaxed normal-case">Posiciona las parejas de forma manual en los casilleros.</span>
                          </div>
                          
                          <div
                            onClick={() => setBracketGenMode('auto')}
                            className={`flex flex-col text-left p-3 rounded-xl border transition-all cursor-pointer ${bracketGenMode === 'auto' ? 'theme-accent-bg/10 theme-accent-border shadow-md' : 'bg-black/20 border-white/5 hover:border-white/20'}`}
                          >
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`text-sm font-black uppercase tracking-widest ${bracketGenMode === 'auto' ? 'theme-accent-text' : 'text-white/60'}`}>⚡ Automático</span>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setShowBracketHelp(true);
                                }}
                                className="text-white/40 hover:text-white transition-colors"
                                title="¿Cómo funciona?"
                              >
                                <HelpCircle size={14} />
                              </button>
                            </div>
                            <span className="text-[10px] text-white/50 leading-relaxed normal-case">Se ubican a las parejas en los casilleros siguiendo el sistema clásico de mérito: 1A vs 2B, etc.</span>
                          </div>
                        </div>
                      </div>

                      <div className="flex flex-col md:flex-row gap-2 w-full justify-end mt-2">
                        <button
                          onClick={() => setShowAddBracket(false)}
                          className="bg-white/5 hover:bg-white/10 text-white/60 hover:text-white font-black px-6 py-2.5 rounded-xl text-xs uppercase tracking-widest transition-all md:mr-auto border border-white/10"
                        >
                          Cancelar
                        </button>
                        <button
                          onClick={() => generateBracket(bracketGenMode === 'auto')}
                          className="theme-accent-bg text-black font-black px-6 py-2.5 rounded-xl text-xs uppercase tracking-widest transition-all shadow-[0_0_20px_rgba(var(--theme-accent),0.3)] hover:scale-105"
                        >
                          Iniciar Eliminatorias
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                );
              })()}



              <SymmetricBracket
                tournament={t}
                allTeams={allTeams}
                clubs={clubs}
                isAdmin={true}
                onEditMatch={handleEditMatchOpen}
                onDropTeam={handleDropTeamToBracket}
                onRemoveTeam={handleRemoveTeamFromBracket}
                onPublishMatch={handlePublishMatch}
                onResetBrackets={handleResetBrackets}
                onResetConfirmStatus={showResetConfirm}
                config={config}
                handleShareCanvas={handleShareCanvas}
                onPlayerClick={onPlayerClick}
                globalMatches={matches}
                actionButtons={t.brackets && t.brackets.length > 0 ? renderActionButtons() : undefined}
                onEditDateTime={(bIdx, mIdx, match) => {
                  setBracketDate(match.date || '');
                  setBracketTime(match.time || '');
                  setEditingDateTime({ bIdx, mIdx, match });
                }}
              />

              {editingMatch && (
                <AdminMatchEditModal
                  m={editingMatch.match}
                  mIdx={editingMatch.mIdx}
                  bIdx={editingMatch.bIdx}
                  t={t}
                  allTeams={allTeams}
                  db={db}
                  getCollectionPath={getCollectionPath}
                  onClose={() => setEditingMatch(null)}
                  onPublish={() => handlePublishMatch(editingMatch.bIdx, editingMatch.mIdx, editingMatch.match)}
                  config={config}
                  allMatches={matches}
                />
              )}

              {editingDateTime && (
                <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm animate-in fade-in duration-200">
                  <div className="bg-[#1e293b]/95 border border-white/10 rounded-2xl p-6 max-w-sm w-full shadow-2xl space-y-4 animate-in zoom-in-95 duration-200">
                    <div className="flex items-center justify-between border-b border-white/10 pb-3">
                      <h3 className="text-sm font-black uppercase tracking-wider text-white flex items-center gap-2">
                        <Clock size={16} className="text-theme-primary" />
                        Fijar Horario
                      </h3>
                      <button onClick={() => setEditingDateTime(null)} className="text-white/40 hover:text-white"><XIcon size={20} /></button>
                    </div>
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                          <label className="text-[10px] uppercase font-bold tracking-widest text-white/60">Fecha</label>
                          <input type="date" value={bracketDate} onChange={e => setBracketDate(e.target.value)} className="w-full bg-[#0f172a] border border-white/10 rounded-xl p-2.5 text-xs text-white focus:border-theme-primary/50 outline-none [&::-webkit-calendar-picker-indicator]:invert-[1] [&::-webkit-calendar-picker-indicator]:opacity-100 [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:hover:scale-150 [&::-webkit-calendar-picker-indicator]:transition-transform [&::-webkit-calendar-picker-indicator]:duration-300" />
                        </div>
                        <div className="space-y-1.5">
                          <label className="text-[10px] uppercase font-bold tracking-widest text-white/60">Hora</label>
                          <input type="time" value={bracketTime} onChange={e => setBracketTime(e.target.value)} className="w-full bg-[#0f172a] border border-white/10 rounded-xl p-2.5 text-xs text-white focus:border-theme-primary/50 outline-none [&::-webkit-calendar-picker-indicator]:invert-[1] [&::-webkit-calendar-picker-indicator]:opacity-100 [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:hover:scale-150 [&::-webkit-calendar-picker-indicator]:transition-transform [&::-webkit-calendar-picker-indicator]:duration-300" />
                        </div>
                      </div>
                      <div className="flex gap-2 pt-2">
                        <button onClick={() => setEditingDateTime(null)} className="flex-1 px-4 py-2 bg-white/5 hover:bg-white/10 text-white/80 rounded-xl text-xs font-black uppercase tracking-widest border border-white/10 transition-colors">Cancelar</button>
                        <button onClick={handleSaveBracketDateTime} className="flex-1 px-4 py-2 theme-primary-bg text-black rounded-xl text-xs font-black uppercase tracking-widest shadow-lg hover:scale-105 active:scale-95 transition-all">Guardar</button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
        </div>
    </div>
  );
}, (prev, next) => {
  if (prev.isExpanded !== next.isExpanded) return false;
  if (prev.isSelected !== next.isSelected) return false;
  
  if (JSON.stringify(prev.t) !== JSON.stringify(next.t)) return false;
  
  const prevMatches = prev.matches.filter(m => m.tournamentId === prev.t.id);
  const nextMatches = next.matches.filter(m => m.tournamentId === next.t.id);
  if (JSON.stringify(prevMatches) !== JSON.stringify(nextMatches)) return false;

  if (prev.allTeams.length !== next.allTeams.length) return false;
  if (prev.players.length !== next.players.length) return false;

  return true;
});
